import { Form } from "@inertiajs/react"
import { Undo2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { unpublishMasterclassPath } from "@/routes"

/**
 * OKS-54 — Unpublish-confirm AlertDialog (UX Spec §4 + Show §2.2).
 *
 * Wraps Inertia `<Form>` (Crew rule). POST to
 * `/brand/masterclasses/:id/unpublish`. Idempotent server-side.
 *
 * `published_at` is INTENTIONALLY preserved server-side (PRD §3 + AC4
 * lock — the body copy promises the original publish date stays).
 */
export interface MasterclassUnpublishConfirmDialogProps {
  masterclassId: number
}

export function MasterclassUnpublishConfirmDialog({
  masterclassId,
}: MasterclassUnpublishConfirmDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger
        render={
          <Button
            variant="secondary"
            data-testid="masterclass-unpublish-trigger"
          >
            <Undo2 className="mr-1 size-4" aria-hidden="true" />
            {t("masterclasses.show.actions.unpublish")}
          </Button>
        }
      />
      <AlertDialogContent data-testid="masterclass-unpublish-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("masterclasses.confirm.unpublish.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("masterclasses.confirm.unpublish.body")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Form
          action={unpublishMasterclassPath(masterclassId)}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <AlertDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="masterclass-unpublish-cancel"
              >
                {t("masterclasses.confirm.unpublish.cancel")}
              </Button>
              <Button
                type="submit"
                variant="secondary"
                disabled={processing}
                autoFocus
                data-testid="masterclass-unpublish-confirm"
              >
                {t("masterclasses.confirm.unpublish.confirm")}
              </Button>
            </AlertDialogFooter>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
